import React, { useState, useEffect } from 'react';
import { saveAddress } from '../services/addressService'; // Import the service function
import '../AddressForm.css'; // Ensure the CSS file is correctly imported

function AddressForm() {
  const initialFormState = {
    userId: '',
    addressCode: '',
    addressText: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    locality: '',
    landmark: '',
    alternatePhone: '',
    addressType: '', // New AddressType field
  };

  const [formData, setFormData] = useState(initialFormState);
  const [showPopup, setShowPopup] = useState(false); // State for Popup visibility
  const [generatedAddressCode, setGeneratedAddressCode] = useState(''); // State for AddressCode
  const [copySuccess, setCopySuccess] = useState(''); // State for copy success message
  const [errorMessage, setErrorMessage] = useState(''); // State to hold detailed error messages

  // Fetch the logged-in user's ID and set it in the form
  useEffect(() => {
    const token = localStorage.getItem('jwtToken');
    if (token) {
      const userId = localStorage.getItem('userId'); // Replace with the actual key used to store userId
      if (userId) {
        setFormData((prevData) => ({
          ...prevData,
          userId: userId, // Set the User ID
        }));
      }
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrorMessage(''); // Clear previous error message
    const token = localStorage.getItem('jwtToken');

    if (!token) {
      setErrorMessage('You need to log in first!');
      return;
    }

    try {
      const response = await saveAddress(formData, token); // Call the service function with token
      setGeneratedAddressCode(response.addressCode); // Set AddressCode in state
      setShowPopup(true); // Show the popup window
    } catch (error) {
      if (error.response) {
        if (error.response.status === 400) {
          setErrorMessage('Invalid input. Please check the form data.');
        } else if (error.response.status === 401) {
          setErrorMessage('Unauthorized. Please log in again.');
        } else if (error.response.status === 500) {
          setErrorMessage('Server error. Please try again later.');
        } else {
          setErrorMessage(`Error: ${error.response.data.message || 'An unknown error occurred.'}`);
        }
      } else if (error.request) {
        setErrorMessage('No response received from the server. Please check your internet connection.');
      } else {
        setErrorMessage(`Error: ${error.message}`);
      }
    }
  };

  const closePopup = () => {
    setShowPopup(false); // Close the popup window
    setFormData(initialFormState); // Reset the form to its initial state
    setCopySuccess(''); // Reset copy success message
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedAddressCode)
      .then(() => {
        setCopySuccess('Address Code copied to clipboard!');
      })
      .catch(() => {
        setCopySuccess('Failed to copy to clipboard.');
      });
  };

  return (
    <div className="address-form-container">
      <form onSubmit={handleSubmit} className="address-form shadow p-4 rounded">
        <div className="form-group mb-3">
          <label htmlFor="userId" className="form-label">User ID</label>
          <input
            type="number"
            className="form-control address-input"
            id="userId"
            name="userId"
            value={formData.userId}
            onChange={handleChange}
            required
            disabled // Make this field not editable
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="addressText" className="form-label">Address Text</label>
          <textarea
            className="form-control address-textarea"
            id="addressText"
            name="addressText"
            rows="3"
            value={formData.addressText}
            onChange={handleChange}
            required
          ></textarea>
        </div>
        <div className="form-group mb-3">
          <label htmlFor="city" className="form-label">City</label>
          <input
            type="text"
            className="form-control address-input"
            id="city"
            name="city"
            value={formData.city}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="state" className="form-label">State</label>
          <input
            type="text"
            className="form-control address-input"
            id="state"
            name="state"
            value={formData.state}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="zipCode" className="form-label">Zip Code</label>
          <input
            type="text"
            className="form-control address-input"
            id="zipCode"
            name="zipCode"
            value={formData.zipCode}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="country" className="form-label">Country</label>
          <input
            type="text"
            className="form-control address-input"
            id="country"
            name="country"
            value={formData.country}
            onChange={handleChange}
            required
          />
        </div>
        {/* New fields */}
        <div className="form-group mb-3">
          <label htmlFor="locality" className="form-label">Locality</label>
          <input
            type="text"
            className="form-control address-input"
            id="locality"
            name="locality"
            value={formData.locality}
            onChange={handleChange}
            required
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="landmark" className="form-label">Landmark</label>
          <input
            type="text"
            className="form-control address-input"
            id="landmark"
            name="landmark"
            value={formData.landmark}
            onChange={handleChange}
            placeholder="(Optional)"
          />
        </div>
        <div className="form-group mb-3">
          <label htmlFor="alternatePhone" className="form-label">Alternate Phone</label>
          <input
            type="text"
            className="form-control address-input"
            id="alternatePhone"
            name="alternatePhone"
            value={formData.alternatePhone}
            onChange={handleChange}
            placeholder="(Optional)"
          />
        </div>
        <div className="form-group mb-3">
          <label className="form-label">Address Type</label>
          <div className="address-type-options">
            <input
              type="radio"
              id="home"
              name="addressType"
              value="Home"
              checked={formData.addressType === 'Home'}
              onChange={handleChange}
              required
            />
            <label htmlFor="home" className="ms-2">Home</label>
            <input
              type="radio"
              id="work"
              name="addressType"
              value="Work"
              checked={formData.addressType === 'Work'}
              onChange={handleChange}
              required
            />
            <label htmlFor="work" className="ms-2">Work</label>
          </div>
        </div>

        <div className="text-center">
          <button type="submit" className="btn btn-primary submit-button">Submit</button>
        </div>
      </form>

      {/* Show Error Message if any */}
      {errorMessage && (
        <div className="alert alert-danger mt-3 error-message">
          <strong>Error:</strong> {errorMessage}
        </div>
      )}

      {/* Popup Window */}
      {showPopup && (
        <div className="popup-overlay">
          <div className="popup-content">
            <h4>Address Added Successfully</h4>
            <p>Your unique Address Code is:</p>
            <p><strong>{generatedAddressCode}</strong></p>
            <button className="btn btn-secondary me-2" onClick={copyToClipboard}>Copy</button>
            <button className="btn btn-success" onClick={closePopup}>OK</button>
            {copySuccess && <p className="mt-2 text-success">{copySuccess}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

export default AddressForm;
